// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Project } from '@/project/api/types';
import {
  UpdateProjectParameters,
  updateProject,
} from '@/project/api/update-project';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UpdateProjectOptions {
  onSuccess?: (project: Project) => void;
  onError?: (error: AxiosError) => void;
}

/**
 * A React hook that updates a project and invalidates the project query.
 *
 * @param key - The key of the project to update.
 * @param options.onSuccess - A callback fired when the project is successfully
 * updated, after the existing project query is invalidated.
 */
export function useUpdateProject(key: string, options?: UpdateProjectOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (parameters: UpdateProjectParameters) => {
      return updateProject(key, parameters);
    },
    onSuccess: async ({ project }) => {
      await queryClient.invalidateQueries({ queryKey: ['project', key] });
      // The window a membership report covers comes from the project's dates.
      await queryClient.invalidateQueries({ queryKey: ['membership'] });

      options?.onSuccess?.(project);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
